import { LocalizedLink } from "@/i18n/language";
import { useTranslation } from "react-i18next";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import PageHeader from "@/components/feature/PageHeader";
import GalleryGrid from "@/components/feature/GalleryGrid";
import { weddingGalleryImages } from "./gallery-data";
import { img } from "@/lib/images";
import { useGalleryCaptions } from "@/hooks/useGalleryCaptions";

const weddingCategories = [
  {
    key: "allgemein",
    slug: "general-wedding-information",
    icon: "ri-information-line",
    image: img("weddings/general-wedding-information/header-general-wedding-information-01.jpg"),
  },
  {
    key: "standesamt",
    slug: "wedding-with-registry-office",
    icon: "ri-file-text-line",
    image: img("weddings/wedding-with-registry-office/header-weddings-with-registry-office-01.jpg"),
  },
  {
    key: "ohne_standesamt",
    slug: "wedding-without-registry-office",
    icon: "ri-heart-2-line",
    image: img("weddings/wedding-without-registry-office/header-weddings-without-registry-office-01.jpg"),
  },
  {
    key: "eheversprechen",
    slug: "vow-renewal",
    icon: "ri-repeat-line",
    image: img("weddings/vow-renewal/header-wedding-vow-renewal-01.jpg"),
  },
  {
    key: "jubilaeum",
    slug: "wedding-anniversary",
    icon: "ri-award-line",
    image: img("weddings/wedding-anniversary/header-wedding-anniversary-01.jpg"),
  },
];

export default function WeddingsPage() {
  const { t } = useTranslation();
  const galleryImages = useGalleryCaptions("weddings", weddingGalleryImages);

  return (
    <div className="bg-background-50 text-foreground-950 min-h-screen">
      <Navbar />
      <main>
        <PageHeader
          eyebrow={t("weddings_eyebrow")}
          title={t("weddings_title")}
          subtitle={t("weddings_overview_subtitle")}
          image={img("weddings/header-weddings-01.jpg")}
          imageAlt="Hochzeit in der Villa Kompiang"
        />

        <section className="py-12 md:py-16 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="text-center max-w-2xl mx-auto mb-8">
              <div className="eyebrow-brand text-xs uppercase tracking-[0.35em] mb-3">
                — {t("weddings_eyebrow")} —
              </div>
              <p className="text-foreground-600 text-sm leading-relaxed">
                {t("weddings_overview_click_hint")}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {weddingCategories.map((cat) => (
                <LocalizedLink
                  key={cat.key}
                  to={`/weddings/${cat.slug}`}
                  className="group bg-background-50 border border-background-200 rounded-lg overflow-hidden hover:border-accent-600 hover:-translate-y-1 transition-all duration-300 cursor-pointer block"
                >
                  <div className="w-full h-48 overflow-hidden bg-background-100">
                    <img
                      src={cat.image}
                      alt={t(`wedding_cat_${cat.key}_title`)}
                      className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-6">
                    <div className="flex items-start gap-3 mb-3">
                      <span className="w-9 h-9 flex items-center justify-center rounded-full bg-accent-100 text-accent-700 flex-shrink-0 mt-0.5">
                        <i className={`${cat.icon} text-base`}></i>
                      </span>
                      <h3 className="font-heading text-lg text-foreground-950 leading-snug group-hover:text-accent-700 transition-colors">
                        {t(`wedding_cat_${cat.key}_title`)}
                      </h3>
                    </div>
                    <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                      {t(`wedding_cat_${cat.key}_desc`)}
                    </p>
                    <span className="text-xs font-medium text-accent-700 group-hover:text-accent-600 transition-colors inline-flex items-center gap-1">
                      {t("read_more")} <i className="ri-arrow-right-line"></i>
                    </span>
                  </div>
                </LocalizedLink>
              ))}
            </div>
          </div>
        </section>

        {/* Gallery */}
        <section className="py-12 md:py-16 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="text-center max-w-3xl mx-auto mb-4">
              <div className="eyebrow-brand text-xs uppercase tracking-[0.35em] mb-3">
                — {t("gallery_tours_eyebrow")} —
              </div>
              <h2 className="font-heading text-3xl md:text-4xl text-foreground-950 leading-[1.12] tracking-tight">
                {t("wedding_cat_galerie_title")}
              </h2>
            </div>
            <p className="text-center text-sm text-foreground-600 mb-10">
              {t("gallery_weddings_hint")}
            </p>
            <GalleryGrid images={galleryImages} />
          </div>
        </section>

        {/* CTA */}
        <section className="py-14 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-8 text-center">
            <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-3">
              {t("weddings_cta_title")}
            </h2>
            <p className="text-foreground-600 text-sm mb-6 max-w-xl mx-auto">
              {t("weddings_cta_sub")}
            </p>
            <LocalizedLink
              to="/wedding-inquiry"
              className="inline-flex items-center gap-2 bg-accent-500 hover:bg-accent-100 text-background-50 hover:text-foreground-950 px-8 py-3.5 rounded-md font-medium whitespace-nowrap cursor-pointer transition-colors"
            >
              <i className="ri-mail-send-line"></i>
              {t("weddings_enquire")}
            </LocalizedLink>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}